import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card, CardContent } from 'components/Card/Card';
import { Container } from 'components/Container/Container';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/Dropdown/Dropdown';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    INVOICES,
    INVOICE_STATS,
    INVOICE_MARK_PAID,
    INVOICE_SEND,
    INVOICE_CANCEL,
    INVOICE_APPROVE,
    INVOICE_DELETE,
} from 'services/invoices';

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as string) || 10;

const SORT_OPTIONS = [
    { value: 'MOST_RECENT', label: 'filters.most_recent' },
    { value: 'LEAST_RECENT', label: 'filters.least_recent' },
    { value: 'A_Z', label: 'filters.a_z' },
    { value: 'Z_A', label: 'filters.z_a' },
    { value: 'HIGHEST_AMOUNT', label: 'filters.highest_amount' },
    { value: 'LOWEST_AMOUNT', label: 'filters.lowest_amount' },
];

const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
        case 'DRAFT':
            return 'secondary';
        case 'PENDING_APPROVAL':
            return 'outline';
        case 'APPROVED':
            return 'default';
        case 'SENT':
            return 'default';
        case 'PAID':
            return 'default';
        case 'OVERDUE':
            return 'destructive';
        case 'CANCELLED':
            return 'secondary';
        case 'CREDITED':
            return 'secondary';
        case 'UNPAID':
            return 'outline';
        default:
            return 'default';
    }
};

const TYPE_LABEL_MAP: Record<string, string> = {
    MEMBERSHIP: 'invoices.typeMembership',
    CUSTOM: 'invoices.typeCustom',
    CONSUMPTION: 'invoices.typeConsumption',
    NEGATIVE_BALANCE: 'invoices.typeNegativeBalance',
    TOP_UP: 'invoices.typeTopUp',
};

const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(num);
};

export const InvoicesList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation('translation');

    // Translated options
    const STATUS_OPTIONS = [
        { value: '', label: t('invoices.allStatuses') },
        { value: 'DRAFT', label: t('status.DRAFT') },
        { value: 'PENDING_APPROVAL', label: t('filters.suggested') },
        { value: 'APPROVED', label: t('status.ACTIVE') },
        { value: 'SENT', label: t('invoices.stats.sent') },
        { value: 'PAID', label: t('status.PAID') },
        { value: 'OVERDUE', label: t('status.OVERDUE') },
        { value: 'CANCELLED', label: t('status.CANCELED') },
    ];

    const TYPE_OPTIONS = [
        { value: '', label: t('invoices.allTypes') },
        { value: 'MEMBERSHIP', label: t('invoices.typeMembership') },
        { value: 'CUSTOM', label: t('invoices.typeCustom') },
        { value: 'CONSUMPTION', label: t('invoices.typeConsumption') },
        { value: 'NEGATIVE_BALANCE', label: t('invoices.typeNegativeBalance') },
        { value: 'TOP_UP', label: t('invoices.typeTopUp') },
    ];

    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');

    const { data, refetch, loading } = useQuery(INVOICES, {
        variables: {
            first: PAGE_SIZE,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
        },
        fetchPolicy: 'cache-and-network',
    });

    const { data: statsData } = useQuery(INVOICE_STATS);

    const [markPaid] = useMutation(INVOICE_MARK_PAID, {
        refetchQueries: [{ query: INVOICES }, { query: INVOICE_STATS }],
    });
    const [sendInvoice] = useMutation(INVOICE_SEND, {
        refetchQueries: [{ query: INVOICES }],
    });
    const [cancelInvoice] = useMutation(INVOICE_CANCEL, {
        refetchQueries: [{ query: INVOICES }, { query: INVOICE_STATS }],
    });
    const [approveInvoice] = useMutation(INVOICE_APPROVE, {
        refetchQueries: [{ query: INVOICES }],
    });
    const [deleteInvoice] = useMutation(INVOICE_DELETE, {
        refetchQueries: [{ query: INVOICES }, { query: INVOICE_STATS }],
    });

    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort');
        const page = searchParams.get('page') || '1';
        const status = searchParams.get('status') || '';
        const type = searchParams.get('type') || '';

        setStatusFilter(status);
        setTypeFilter(type);

        refetch({
            search: search || undefined,
            sort: sort || undefined,
            offset: (parseInt(page) - 1) * PAGE_SIZE,
            status: status || undefined,
            type: type || undefined,
        });
    }, [searchParams, refetch]);

    const handleStatusChange = (value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('status', value);
        } else {
            params.delete('status');
        }
        params.set('page', '1');
        setSearchParams(params);
    };

    const handleTypeChange = (value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('type', value);
        } else {
            params.delete('type');
        }
        params.set('page', '1');
        setSearchParams(params);
    };

    const handleAction = async (action: string, invoice: any) => {
        try {
            switch (action) {
                case 'mark_paid':
                    await markPaid({ variables: { id: invoice.id } });
                    break;
                case 'send':
                    await sendInvoice({ variables: { id: invoice.id } });
                    break;
                case 'approve':
                    await approveInvoice({ variables: { id: invoice.id } });
                    break;
                case 'cancel':
                    const reason = window.prompt(t('invoices.enterCancellationReason'));
                    if (reason) {
                        await cancelInvoice({ variables: { id: invoice.id, reason } });
                    }
                    break;
                case 'delete':
                    if (window.confirm(t('invoices.confirmDelete'))) {
                        await deleteInvoice({ variables: { id: invoice.id } });
                    }
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error('Action failed:', error);
            alert(t('invoices.actionFailed'));
        }
    };

    const getRecipientName = (invoice: any) => {
        if (invoice.company) {
            return invoice.company.name;
        }
        if (invoice.member) {
            return `${invoice.member.lastName}, ${invoice.member.firstName}`;
        }
        return '-';
    };

    const getAvailableActions = (invoice: any) => {
        const actions: { label: string; action: string; variant?: string }[] = [];

        switch (invoice.status) {
            case 'DRAFT':
                actions.push({ label: t('invoices.edit'), action: 'edit' });
                actions.push({ label: t('invoices.delete'), action: 'delete', variant: 'destructive' });
                break;
            case 'PENDING_APPROVAL':
                actions.push({ label: t('invoices.approve'), action: 'approve' });
                actions.push({ label: t('invoices.reject'), action: 'reject' });
                break;
            case 'APPROVED':
                actions.push({ label: t('invoices.send'), action: 'send' });
                actions.push({ label: t('invoices.cancel'), action: 'cancel', variant: 'destructive' });
                break;
            case 'SENT':
            case 'UNPAID':
                actions.push({ label: t('invoices.markPaid'), action: 'mark_paid' });
                actions.push({ label: t('invoices.cancel'), action: 'cancel', variant: 'destructive' });
                break;
            case 'OVERDUE':
                actions.push({ label: t('invoices.markPaid'), action: 'mark_paid' });
                actions.push({ label: t('invoices.sendReminder'), action: 'reminder' });
                actions.push({ label: t('invoices.cancel'), action: 'cancel', variant: 'destructive' });
                break;
            default:
                break;
        }

        return actions;
    };

    const stats = statsData?.invoiceStats;
    const invoices = normalizeArray(data?.invoices);

    return (
        <Container>
            <Breadcrumbs crumbs={[t('breadcrumbs.dashboard'), t('invoices.title')]} />
            <div className="flex justify-between items-center mb-6">
                <PageTitle>{t('invoices.title')}</PageTitle>
                <Button onClick={() => navigate('/invoices/new')} disabled>
                    {t('invoices.createInvoice')}
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                    <Card className="bg-white">
                        <CardContent className="p-4 text-center">
                            <div className="text-3xl font-bold text-zinc-800">{stats.totalCount}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.total')}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="p-4 text-center">
                            <div className="text-3xl font-bold text-zinc-400">{stats.draftCount}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.draft')}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="p-4 text-center">
                            <div className="text-3xl font-bold text-amber-500">{stats.pendingApprovalCount}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.pending')}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="p-4 text-center">
                            <div className="text-3xl font-bold text-blue-500">{stats.sentCount}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.sent')}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="p-4 text-center">
                            <div className="text-3xl font-bold text-emerald-500">{stats.paidCount}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.paid')}</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-rose-50 to-white border-rose-200">
                        <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-rose-600">
                                {formatCurrency(stats.outstandingAmount || 0)}
                            </div>
                            <div className="text-sm text-zinc-500 mt-1">{t('invoices.stats.outstanding')}</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card className="mb-6">
                <CardContent className="p-4">
                    <div className="flex gap-4 flex-wrap items-center">
                        <SearchFilter placeholder={t('invoices.searchPlaceholder')} />
                        <SortFilter options={SORT_OPTIONS} />
                        <select
                            value={statusFilter}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            className="border border-zinc-200 rounded-lg px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={typeFilter}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            className="border border-zinc-200 rounded-lg px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        >
                            {TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card className="overflow-x-auto">
                <Table className="min-w-[900px]">
                    <TableHeader>
                        <TableRow className="bg-zinc-50">
                            <TableHead className="font-semibold">{t('invoices.table.invoiceNumber')}</TableHead>
                            <TableHead className="font-semibold">{t('invoices.table.recipient')}</TableHead>
                            <TableHead className="font-semibold">{t('invoices.table.type')}</TableHead>
                            <TableHead className="text-right font-semibold">{t('invoices.table.amount')}</TableHead>
                            <TableHead className="font-semibold">{t('invoices.table.dueDate')}</TableHead>
                            <TableHead className="font-semibold">{t('invoices.table.status')}</TableHead>
                            <TableHead className="text-right font-semibold">{t('invoices.table.actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12">
                                    <div className="text-zinc-400">{t('invoices.loadingInvoices')}</div>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && invoices.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12">
                                    <div className="text-zinc-400">{t('invoices.noInvoicesFound')}</div>
                                    <div className="text-sm text-zinc-300 mt-1">{t('invoices.tryAdjustingFilters')}</div>
                                </TableCell>
                            </TableRow>
                        )}
                        {invoices.map((invoice: any) => (
                            <TableRow
                                key={invoice.id}
                                className="text-zinc-700 hover:bg-rose-50/50 cursor-pointer transition-colors"
                                onClick={() => navigate(`/invoices/${invoice.id}`)}
                            >
                                <TableCell className="font-mono text-sm font-medium">{invoice.invoiceId}</TableCell>
                                <TableCell className="font-medium">{getRecipientName(invoice)}</TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {TYPE_LABEL_MAP[invoice.type] ? t(TYPE_LABEL_MAP[invoice.type]) : invoice.type}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                    {formatCurrency(invoice.amount)}
                                </TableCell>
                                <TableCell className="text-zinc-500">
                                    {invoice.dueDate ? dayjs(invoice.dueDate).format('DD.MM.YYYY') : '-'}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={getStatusBadgeVariant(invoice.status)}>
                                        {t(`status.${invoice.status}`, { defaultValue: invoice.status })}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                                        {getAvailableActions(invoice).length > 0 && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" size="sm">
                                                        <Icon name="three-dots" className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {getAvailableActions(invoice).map((action, idx) => (
                                                        <React.Fragment key={action.action}>
                                                            {action.variant === 'destructive' && idx > 0 && (
                                                                <DropdownMenuSeparator />
                                                            )}
                                                            <DropdownMenuItem
                                                                className={
                                                                    action.variant === 'destructive'
                                                                        ? 'text-red-600 focus:text-red-600'
                                                                        : ''
                                                                }
                                                                onClick={() => {
                                                                    if (action.action === 'edit') {
                                                                        navigate(`/invoices/${invoice.id}/edit`);
                                                                    } else {
                                                                        handleAction(action.action, invoice);
                                                                    }
                                                                }}
                                                            >
                                                                {action.label}
                                                            </DropdownMenuItem>
                                                        </React.Fragment>
                                                    ))}
                                                    {invoice.document?.url && (
                                                        <>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={() =>
                                                                    window.open(invoice.document.url, '_blank')
                                                                }
                                                            >
                                                                <Icon name="download" className="mr-2 h-4 w-4" />
                                                                {t('invoices.downloadPdf')}
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                        {getAvailableActions(invoice).length === 0 && invoice.document?.url && (
                                            <a
                                                href={invoice.document.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Button variant="outline" size="sm">
                                                    <Icon name="download" className="h-4 w-4" />
                                                </Button>
                                            </a>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
            
            <div className="mt-6">
                <Pagination pageInfo={data?.invoices?.pageInfo} total={data?.invoices?.totalCount} />
            </div>
        </Container>
    );
};
